import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Undo2, Square, Circle, MessageSquare, Check, X, Plus, Loader2, ChevronDown, ChevronUp, Target, StickyNote, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { createEvent } from '../lib/storage';
import { fetchSessionParts, createSessionPart } from '../lib/sessionPartsApi';
import { formatTime, cn, generateId } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useCloudSync } from '../contexts/CloudSyncContext';
import { safeGet } from '../lib/safeFetch';
import SyncStatusIndicator from '../components/SyncStatusIndicator';
import { SpeechToTextButton } from '../components/SpeechToTextButton';

export default function LiveObservation() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { isCoachDeveloper } = useAuth();
  const { getSession, saveSession: cloudSaveSession, setCurrentSession, getCachedSession } = useCloudSync();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [flashEvent, setFlashEvent] = useState(null);
  const [showAddPart, setShowAddPart] = useState(false);
  const [newPartName, setNewPartName] = useState('');
  
  // New part dialog state
  const [showAddPartDialog, setShowAddPartDialog] = useState(false);
  const [availableParts, setAvailableParts] = useState([]);
  const [selectedDefaultPart, setSelectedDefaultPart] = useState('custom');
  const [customPartName, setCustomPartName] = useState('');
  const [addAsDefault, setAddAsDefault] = useState(false);
  const [savingPart, setSavingPart] = useState(false);
  
  // Coach info state (Phase 4)
  const [coachInfo, setCoachInfo] = useState(null);
  const [showCoachInfo, setShowCoachInfo] = useState(false);
  
  // Observer notes state (Phase 4)
  const [observerNotes, setObserverNotes] = useState([]);
  const [showNotesPanel, setShowNotesPanel] = useState(false); // Collapsed by default
  const [currentNote, setCurrentNote] = useState('');
  
  // Session start time for relative timing (Phase 5)
  const [sessionStartTime, setSessionStartTime] = useState(null);
  
  const timerRef = useRef(null);
  const lastBallStateChange = useRef(null);
  const partStartTime = useRef(null);

  // Load session from cloud (with cache support)
  useEffect(() => {
    const loadSession = async () => {
      setLoading(true);
      
      // First check cache for immediate display
      const cached = getCachedSession(sessionId);
      if (cached) {
        setSession(cached);
        setCurrentSession(cached);
        // Resume if session was active
        if (cached.status === 'active' && cached.startTime) {
          const elapsed = Math.floor((Date.now() - new Date(cached.startTime).getTime()) / 1000);
          setElapsedTime(elapsed);
          setIsRunning(true);
        } else if (cached.totalDuration > 0) {
          setElapsedTime(cached.totalDuration);
        }
      }
      
      // Then fetch fresh data from cloud
      try {
        const loaded = await getSession(sessionId, !cached); // Force refresh if no cache
        if (!loaded) {
          toast.error('Session not found');
          navigate('/');
          return;
        }
        
        setSession(loaded);
        setCurrentSession(loaded);
        loadAvailableParts();
        
        // Load coach info if coach is assigned (Phase 4)
        if (loaded.coachId) {
          loadCoachInfo(loaded.coachId);
        }
        
        // Load existing observer notes if any
        if (loaded.observerNotes && Array.isArray(loaded.observerNotes)) {
          setObserverNotes(loaded.observerNotes);
        }
        
        // Resume if session was active
        if (loaded.status === 'active' && loaded.startTime) {
          const elapsed = Math.floor((Date.now() - new Date(loaded.startTime).getTime()) / 1000);
          setElapsedTime(elapsed);
          setIsRunning(true);
          
          // Restore session start timestamp for relative timing
          if (loaded.sessionStartTimestamp) {
            setSessionStartTime(loaded.sessionStartTimestamp);
          } else {
            // Calculate from startTime if not stored
            setSessionStartTime(new Date(loaded.startTime).getTime());
          }
        } else if (loaded.totalDuration > 0) {
          setElapsedTime(loaded.totalDuration);
        }
      } catch (err) {
        console.error('Failed to load session:', err);
        if (!cached) {
          toast.error('Failed to load session');
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadSession();
  }, [sessionId, navigate, getSession, setCurrentSession, getCachedSession]);

  const loadAvailableParts = async () => {
    try {
      const parts = await fetchSessionParts();
      setAvailableParts(parts);
    } catch (err) {
      console.error('Failed to load session parts:', err);
    }
  };

  const loadCoachInfo = async (coachId) => {
    if (!coachId) return;
    try {
      const result = await safeGet(`/api/coaches/${coachId}`);
      if (result.ok && result.data) {
        setCoachInfo(result.data);
      }
    } catch (err) {
      console.error('Failed to load coach info:', err);
    }
  };

  // Timer logic
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning]);

  // Auto-save every 5 seconds
  useEffect(() => {
    if (!session || !isRunning) return;
    
    const saveInterval = setInterval(() => {
      handleSaveSession();
    }, 5000);
    
    return () => clearInterval(saveInterval);
  }, [session, isRunning, elapsedTime]);

  const handleSaveSession = useCallback(async () => {
    if (!session) return;
    
    const updated = {
      ...session,
      totalDuration: elapsedTime,
      updatedAt: new Date().toISOString()
    };
    
    // Update local state and cache
    setSession(updated);
    setCurrentSession(updated);
    
    // Save to cloud (which also saves to localStorage)
    try {
      await cloudSaveSession(updated);
    } catch (err) {
      console.error('Cloud sync failed:', err);
    }
  }, [session, elapsedTime, cloudSaveSession, setCurrentSession]);

  const handleStart = () => {
    if (!isRunning) {
      const now = new Date().toISOString();
      const startTimestamp = Date.now();
      
      // Set session start time for relative timing
      setSessionStartTime(startTimestamp);
      
      setSession(prev => ({
        ...prev,
        status: 'active',
        startTime: prev.startTime || now,
        sessionStartTimestamp: prev.sessionStartTimestamp || startTimestamp, // Store for relative timing
        sessionParts: (prev.sessionParts || []).map(p => 
          p.id === prev.activePartId ? { ...p, startTime: now, used: true } : p
        )
      }));
      setIsRunning(true);
      lastBallStateChange.current = startTimestamp;
      partStartTime.current = startTimestamp;
      toast.success('Observation started');
    }
  };

  // Show confirmation dialog before ending
  const handleStopClick = () => {
    if (isRunning) {
      setShowEndConfirm(true);
    }
  };

  // Actually end the session after confirmation
  const handleConfirmEnd = async () => {
    setIsSaving(true);
    
    try {
      // Calculate final ball rolling time for current state
      let finalSession;
      
      // Keep all session parts (don't filter by 'used' - this was causing data loss)
      const allParts = (session.sessionParts || []).map(p => {
        if (p.id === session.activePartId && lastBallStateChange.current) {
          const duration = (Date.now() - lastBallStateChange.current) / 1000;
          const ballTimeKey = session.ballRolling ? 'ballRollingTime' : 'ballNotRollingTime';
          return { ...p, [ballTimeKey]: (p[ballTimeKey] || 0) + duration, endTime: new Date().toISOString() };
        }
        return p;
      });
      
      const endTime = new Date().toISOString();
      
      if (lastBallStateChange.current) {
        const duration = (Date.now() - lastBallStateChange.current) / 1000;
        const ballTimeKey = session.ballRolling ? 'ballRollingTime' : 'ballNotRollingTime';
        
        finalSession = {
          ...session,
          [ballTimeKey]: (session[ballTimeKey] || 0) + duration,
          sessionParts: allParts,
          status: 'completed',
          endTime: endTime,
          totalDuration: elapsedTime,
          updatedAt: endTime
        };
      } else {
        finalSession = {
          ...session,
          sessionParts: allParts,
          status: 'completed',
          endTime: endTime,
          totalDuration: elapsedTime,
          updatedAt: endTime
        };
      }
      
      console.log('[LiveObservation] Saving completed session:', {
        id: finalSession.id,
        status: finalSession.status,
        events: finalSession.events?.length,
        duration: finalSession.totalDuration
      });
      
      // Update state and cache
      setSession(finalSession);
      setCurrentSession(finalSession);
      
      // Wait for cloud save to complete
      const saveResult = await cloudSaveSession(finalSession);
      
      console.log('[LiveObservation] Save result:', saveResult);
      
      setIsRunning(false);
      setShowEndConfirm(false);
      
      if (saveResult.success || saveResult.queued) {
        toast.success(saveResult.success ? 'Session completed and saved!' : 'Session completed! Will sync when online.');
        
        // Navigate directly to review page (reflection is now in Reflections tab)
        navigate(`/session/${sessionId}/review`);
      } else {
        // Save failed - ask user what to do
        toast.error('Failed to save to cloud. Please try again or check your connection.');
        setIsSaving(false);
        return; // Don't navigate away
      }
    } catch (err) {
      console.error('[LiveObservation] Failed to save session:', err);
      toast.error('Failed to save session. Please try again.');
      setIsSaving(false);
      // Don't close dialog or navigate - let user retry
      return;
    }
  };

  // Format relative time (milliseconds to MM:SS)
  const formatRelativeTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleEventTap = (eventType) => {
    if (!isRunning) {
      handleStart();
    }
    
    // Calculate relative timestamp (ms from session start)
    const currentStartTime = sessionStartTime || session?.sessionStartTimestamp || Date.now();
    const relativeTimestamp = Date.now() - currentStartTime;
    
    const event = createEvent(
      eventType.id,
      eventType.name,
      session.activePartId,
      session.ballRolling
    );
    
    // Add relative timestamp to the event
    event.relativeTimestamp = relativeTimestamp;
    
    setSession(prev => ({
      ...prev,
      events: [...prev.events, event]
    }));
    
    setLastEvent(event);
    setFlashEvent(eventType.id);
    setTimeout(() => setFlashEvent(null), 200);
    
    toast.success(`${eventType.name} logged`, { duration: 1500 });
  };

  const handleDescriptorToggle = (groupNum, descriptorId) => {
    if (!lastEvent) return;
    
    const key = groupNum === 1 ? 'descriptors1' : 'descriptors2';
    
    setSession(prev => ({
      ...prev,
      events: prev.events.map(e => {
        if (e.id === lastEvent.id) {
          const current = e[key];
          const updated = current.includes(descriptorId)
            ? current.filter(d => d !== descriptorId)
            : [...current, descriptorId];
          return { ...e, [key]: updated };
        }
        return e;
      })
    }));
    
    // Update lastEvent state
    setLastEvent(prev => {
      const current = prev[key];
      const updated = current.includes(descriptorId)
        ? current.filter(d => d !== descriptorId)
        : [...current, descriptorId];
      return { ...prev, [key]: updated };
    });
  };

  const handleUndo = () => {
    const events = session.events || [];
    if (events.length === 0) return;
    
    const removed = events[events.length - 1];
    setSession(prev => ({
      ...prev,
      events: (prev.events || []).slice(0, -1)
    }));
    
    setLastEvent(events.length > 1 ? events[events.length - 2] : null);
    toast.info(`Undid: ${removed.eventTypeName}`);
  };

  const handleBallToggle = () => {
    const now = Date.now();
    
    if (lastBallStateChange.current && isRunning) {
      const duration = (now - lastBallStateChange.current) / 1000;
      const ballTimeKey = session.ballRolling ? 'ballRollingTime' : 'ballNotRollingTime';
      
      setSession(prev => ({
        ...prev,
        [ballTimeKey]: prev[ballTimeKey] + duration,
        sessionParts: (prev.sessionParts || []).map(p => 
          p.id === prev.activePartId 
            ? { ...p, [ballTimeKey]: p[ballTimeKey] + duration }
            : p
        ),
        ballRolling: !prev.ballRolling,
        ballRollingLog: [...(prev.ballRollingLog || []), {
          timestamp: new Date().toISOString(),
          state: !prev.ballRolling,
          partId: prev.activePartId
        }]
      }));
    } else {
      setSession(prev => ({
        ...prev,
        ballRolling: !prev.ballRolling
      }));
    }
    
    lastBallStateChange.current = now;
  };

  const handlePartChange = (partId) => {
    if (partId === session.activePartId) return; // Already on this part
    
    const now = Date.now();
    const nowIso = new Date().toISOString();
    
    if (isRunning && lastBallStateChange.current) {
      // Calculate time spent in current ball state for current part
      const duration = (now - lastBallStateChange.current) / 1000;
      const ballTimeKey = session.ballRolling ? 'ballRollingTime' : 'ballNotRollingTime';
      
      setSession(prev => ({
        ...prev,
        // Update overall session ball time
        [ballTimeKey]: (prev[ballTimeKey] || 0) + duration,
        // Update parts - end current, start new, mark as used
        sessionParts: (prev.sessionParts || []).map(p => {
          if (p.id === prev.activePartId) {
            return { ...p, [ballTimeKey]: (p[ballTimeKey] || 0) + duration, endTime: nowIso };
          }
          if (p.id === partId) {
            return { ...p, startTime: p.startTime || nowIso, used: true };
          }
          return p;
        }),
        activePartId: partId
      }));
      
      // Reset the ball state timer for the new part
      lastBallStateChange.current = now;
    } else {
      setSession(prev => ({
        ...prev,
        sessionParts: (prev.sessionParts || []).map(p => 
          p.id === partId ? { ...p, startTime: p.startTime || nowIso, used: true } : p
        ),
        activePartId: partId
      }));
      lastBallStateChange.current = now;
    }
    
    partStartTime.current = now;
  };

  const handleAddPart = () => {
    if (!newPartName.trim()) return;
    
    const newPart = {
      id: generateId('part'),
      name: newPartName.trim(),
      order: (session.sessionParts || []).length,
      startTime: null,
      endTime: null,
      ballRollingTime: 0,
      ballNotRollingTime: 0,
      isCustom: true
    };
    
    setSession(prev => ({
      ...prev,
      sessionParts: [...(prev.sessionParts || []), newPart]
    }));
    
    setNewPartName('');
    setShowAddPart(false);
    toast.success(`Added "${newPart.name}"`);
  };

  // Open add part dialog
  const openAddPartDialog = () => {
    setSelectedDefaultPart('custom');
    setCustomPartName('');
    setAddAsDefault(false);
    setShowAddPartDialog(true);
  };

  // Handle adding part from dialog
  const handleAddPartFromDialog = async () => {
    let partName = '';
    
    if (selectedDefaultPart === 'custom') {
      if (!customPartName.trim()) {
        toast.error('Please enter a part name');
        return;
      }
      partName = customPartName.trim();
    } else {
      const defaultPart = availableParts.find(p => p.part_id === selectedDefaultPart);
      if (defaultPart) {
        partName = defaultPart.name;
      }
    }

    if (!partName) {
      toast.error('Please select or enter a part name');
      return;
    }

    setSavingPart(true);
    try {
      // If adding custom as new default (Coach Developer only)
      if (selectedDefaultPart === 'custom' && addAsDefault && isCoachDeveloper()) {
        await createSessionPart(partName, true);
        await loadAvailableParts();
      }

      const newPart = {
        id: generateId('part'),
        name: partName,
        order: (session.sessionParts || []).length,
        startTime: null,
        endTime: null,
        ballRollingTime: 0,
        ballNotRollingTime: 0,
        isDefault: selectedDefaultPart !== 'custom',
        isCustom: selectedDefaultPart === 'custom'
      };
      
      setSession(prev => ({
        ...prev,
        sessionParts: [...(prev.sessionParts || []), newPart]
      }));
      
      setShowAddPartDialog(false);
      toast.success(`Added "${partName}"`);
    } catch (err) {
      toast.error(err.message || 'Failed to add part');
    } finally {
      setSavingPart(false);
    }
  };

  const handleAddNote = () => {
    if (!lastEvent || !noteText.trim()) return;
    
    setSession(prev => ({
      ...prev,
      events: prev.events.map(e => 
        e.id === lastEvent.id ? { ...e, note: noteText } : e
      )
    }));
    
    setLastEvent(prev => ({ ...prev, note: noteText }));
    setNoteText('');
    setShowNoteInput(false);
    toast.success('Note added');
  };

  // Observer Notes handlers (Phase 4)
  const handleAddObserverNote = () => {
    if (!currentNote.trim()) return;
    
    // Calculate relative timestamp (ms from session start)
    const currentStartTime = sessionStartTime || session?.sessionStartTimestamp || Date.now();
    const relativeTimestamp = Date.now() - currentStartTime;
    
    const newNote = {
      id: generateId('note'),
      text: currentNote.trim(),
      timestamp: new Date().toISOString(),
      relativeTimestamp: relativeTimestamp, // Store relative time
      partId: session.activePartId,
      partName: activePart?.name || 'Unknown'
    };
    
    const updatedNotes = [...observerNotes, newNote];
    setObserverNotes(updatedNotes);
    setCurrentNote('');
    
    // Save notes to session
    setSession(prev => ({
      ...prev,
      observerNotes: updatedNotes
    }));
    
    // Minimize the notes panel after saving
    setShowNotesPanel(false);
    
    toast.success('Note saved', { duration: 1500 });
  };

  const handleDeleteObserverNote = (noteId) => {
    const updatedNotes = observerNotes.filter(n => n.id !== noteId);
    setObserverNotes(updatedNotes);
    
    setSession(prev => ({
      ...prev,
      observerNotes: updatedNotes
    }));
  };

  if (!session) return null;

  const activePart = (session.sessionParts || []).find(p => p.id === session.activePartId);
  const activeTargets = coachInfo?.targets?.filter(t => t.status === 'active') || [];

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col safe-area-inset">
      {/* Status Bar - Always Visible */}
      <header className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 safe-area-top">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => {
                handleSaveSession();
                navigate('/');
              }}
              className="shrink-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-semibold text-slate-900 font-['Manrope'] text-sm sm:text-base truncate">{session.name}</h1>
              <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-wrap">
                <Badge variant="outline" className="font-medium text-xs">
                  {activePart?.name || 'No Part'}
                </Badge>
                {session.includeBallRolling !== false && (
                  <>
                    <span className="text-slate-400 hidden sm:inline">•</span>
                    <span className={cn(
                      "flex items-center gap-1 font-medium",
                      session.ballRolling ? "text-orange-600" : "text-slate-500"
                    )}>
                      {session.ballRolling ? (
                        <>
                          <Circle className="w-3 h-3 fill-current ball-rolling-indicator" />
                          <span className="hidden sm:inline">Ball Rolling</span>
                          <span className="sm:hidden">Rolling</span>
                        </>
                      ) : (
                        <>
                          <Square className="w-3 h-3" />
                          <span className="hidden sm:inline">Ball Stopped</span>
                          <span className="sm:hidden">Stopped</span>
                        </>
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Timer */}
          <div className="text-center shrink-0">
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold font-mono timer-display text-slate-900" data-testid="session-timer">
              {formatTime(elapsedTime)}
            </div>
            <SyncStatusIndicator className="justify-center" />
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {session.events.length > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleUndo}
                className="undo-btn hidden sm:flex"
                data-testid="undo-btn"
              >
                <Undo2 className="w-4 h-4 mr-1" />
                Undo
              </Button>
            )}
            {session.events.length > 0 && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleUndo}
                className="undo-btn sm:hidden"
                data-testid="undo-btn-mobile"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
            )}
            {!isRunning ? (
              <Button 
                onClick={handleStart}
                className="bg-green-600 hover:bg-green-700 text-sm sm:text-base px-3 sm:px-4"
                data-testid="start-btn"
              >
                Start
              </Button>
            ) : (
              <Button 
                onClick={handleStopClick}
                variant="destructive"
                className="text-sm sm:text-base px-2 sm:px-4"
                data-testid="stop-btn"
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : null}
                <span className="hidden sm:inline">End Session</span>
                <span className="sm:hidden">End</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Coach Info & Targets (Phase 4) */}
      {coachInfo && (
        <Collapsible open={showCoachInfo} onOpenChange={setShowCoachInfo}>
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-slate-200">
            <CollapsibleTrigger asChild>
              <button className="w-full px-4 py-2 flex items-center justify-between hover:bg-blue-100/50 transition-colors">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-slate-700">{coachInfo.name}</span>
                  {coachInfo.role_title && (
                    <Badge variant="secondary" className="text-xs">
                      {coachInfo.role_title}
                    </Badge>
                  )}
                  {activeTargets.length > 0 && (
                    <Badge className="bg-orange-100 text-orange-700 text-xs">
                      <Target className="w-3 h-3 mr-1" />
                      {activeTargets.length} Active Targets
                    </Badge>
                  )}
                </div>
                {showCoachInfo ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 pb-3 space-y-3">
                {activeTargets.length > 0 ? (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Development Targets
                    </h4>
                    <div className="space-y-2">
                      {activeTargets.map((target, idx) => (
                        <div 
                          key={target.id || idx} 
                          className="bg-white rounded-lg px-3 py-2 border border-orange-200 flex items-start gap-2"
                        >
                          <Target className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                          <span className="text-sm text-slate-700">{target.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No active development targets set for this coach.</p>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {/* Session Parts Tabs */}
      <div className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-6">
        <div className="flex gap-1 overflow-x-auto py-2 items-center tabs-responsive">
          {(session.sessionParts || []).map((part) => (
            <button
              key={part.id}
              onClick={() => handlePartChange(part.id)}
              className={cn(
                "px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap",
                "min-h-[44px] touch-action-manipulation",
                part.id === session.activePartId
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              )}
              data-testid={`part-btn-${part.id}`}
            >
              {part.name}
            </button>
          ))}
          <button
            onClick={openAddPartDialog}
            className="px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all whitespace-nowrap flex items-center gap-1 min-h-[44px]"
            data-testid="add-part-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Part</span>
          </button>
        </div>
      </div>

      {/* Add Part Dialog */}
      <Dialog open={showAddPartDialog} onOpenChange={setShowAddPartDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Session Part</DialogTitle>
            <DialogDescription>
              Select from default parts or create a custom one.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Select a Part</Label>
              <Select value={selectedDefaultPart} onValueChange={setSelectedDefaultPart}>
                <SelectTrigger className="mt-1" data-testid="select-default-part">
                  <SelectValue placeholder="Choose a part..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom Part</SelectItem>
                  {availableParts.filter(p => p.is_default).map(part => {
                    const isAlreadyAdded = (session.sessionParts || []).some(sp => sp.name === part.name);
                    return (
                      <SelectItem 
                        key={part.part_id} 
                        value={part.part_id}
                        disabled={isAlreadyAdded}
                      >
                        {part.name} {isAlreadyAdded && "(already added)"}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {selectedDefaultPart === 'custom' && (
              <>
                <div>
                  <Label htmlFor="custom-part-name">Custom Part Name</Label>
                  <Input
                    id="custom-part-name"
                    value={customPartName}
                    onChange={(e) => setCustomPartName(e.target.value)}
                    placeholder="e.g., Cool Down"
                    className="mt-1"
                    data-testid="custom-part-input"
                  />
                </div>
                
                {isCoachDeveloper() && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="add-as-default"
                      checked={addAsDefault}
                      onChange={(e) => setAddAsDefault(e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor="add-as-default" className="text-sm text-slate-600 cursor-pointer">
                      Add as new default (available globally)
                    </Label>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPartDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddPartFromDialog} 
              disabled={savingPart || (selectedDefaultPart === 'custom' && !customPartName.trim())}
            >
              {savingPart ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Part
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ball Rolling Toggle - Only show if includeBallRolling is enabled */}
      {session.includeBallRolling !== false && (
        <div className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-6 py-2 sm:py-3">
          <button
            onClick={handleBallToggle}
            className={cn(
              "w-full py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all min-h-[56px] sm:min-h-[64px]",
              session.ballRolling
                ? "bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700"
                : "bg-slate-600 text-white hover:bg-slate-700 active:bg-slate-800"
            )}
            data-testid="ball-toggle-btn"
          >
            {session.ballRolling ? (
              <span className="flex items-center justify-center gap-2">
                <Circle className="w-4 sm:w-5 h-4 sm:h-5 fill-current ball-rolling-indicator" />
                Ball Rolling
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Square className="w-4 sm:w-5 h-4 sm:h-5" />
                Ball Not Rolling
              </span>
            )}
          </button>
        </div>
      )}

      {/* Code Window Grid */}
      <main className="flex-1 p-3 sm:p-4 lg:p-6 overflow-auto">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 max-w-6xl mx-auto">
          {/* Column 1: Coach Interventions (Yellow) */}
          <div className="space-y-2 sm:space-y-3 col-span-2 sm:col-span-1">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
              Coach Interventions
            </div>
            {/* Adaptive grid: 2 columns for 4+ buttons, smaller buttons when 5+ */}
            {(() => {
              const interventions = session.interventionTypes || session.eventTypes || [];
              const count = interventions.length;
              // Use 2 columns when 5+ items, or on mobile with 4+ items
              const useMultiColumn = count > 4;
              // Smaller buttons when 5+ items
              const compactMode = count > 4;
              
              return (
                <div className={cn(
                  "grid gap-2",
                  useMultiColumn 
                    ? "grid-cols-2" 
                    : "grid-cols-2 sm:grid-cols-1"
                )}>
                  {interventions.map((eventType) => (
                    <button
                      key={eventType.id}
                      onClick={() => handleEventTap(eventType)}
                      className={cn(
                        "w-full rounded-xl font-bold shadow-md transition-all",
                        "bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-slate-900",
                        "border-b-4 border-yellow-600 active:border-b-0 active:translate-y-1",
                        flashEvent === eventType.id && "event-logged",
                        // Adaptive sizing based on count
                        compactMode 
                          ? "h-12 sm:h-14 text-xs sm:text-sm" 
                          : "h-16 sm:h-20 lg:h-24 text-sm sm:text-base lg:text-lg"
                      )}
                      data-testid={`event-btn-${eventType.id}`}
                    >
                      {eventType.name}
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Column 2: Descriptor Group 1 (Blue) */}
          <div className="space-y-2 sm:space-y-3">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
              {session.descriptorGroup1?.name || 'Descriptor Group 1'}
            </div>
            {/* Adaptive grid for descriptor group 1 */}
            {(() => {
              const descriptors = session.descriptorGroup1?.descriptors || [];
              const count = descriptors.length;
              const useMultiColumn = count > 4;
              const compactMode = count > 4;
              
              return (
                <div className={cn(
                  "grid gap-2",
                  useMultiColumn ? "grid-cols-2" : "grid-cols-1"
                )}>
                  {descriptors.map((desc) => {
                    const isSelected = lastEvent?.descriptors1?.includes(desc.id);
                    return (
                      <button
                        key={desc.id}
                        onClick={() => handleDescriptorToggle(1, desc.id)}
                        disabled={!lastEvent}
                        className={cn(
                          "w-full rounded-xl font-semibold transition-all border-2",
                          !lastEvent && "opacity-40 cursor-not-allowed",
                          isSelected
                            ? "bg-sky-500 text-white border-sky-700 shadow-lg"
                            : "bg-sky-400 text-white border-transparent hover:bg-sky-500 active:bg-sky-600",
                          compactMode
                            ? "h-12 sm:h-14 text-xs sm:text-sm"
                            : "h-14 sm:h-16 lg:h-20 text-sm sm:text-base"
                        )}
                        data-testid={`desc1-btn-${desc.id}`}
                      >
                        <span className="flex items-center justify-center gap-1 sm:gap-2">
                          {isSelected && <Check className="w-3 sm:w-4 h-3 sm:h-4" />}
                          {desc.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Column 3: Descriptor Group 2 (Green) */}
          <div className="space-y-2 sm:space-y-3">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
              {session.descriptorGroup2?.name || 'Descriptor Group 2'}
            </div>
            {/* Adaptive grid for descriptor group 2 */}
            {(() => {
              const descriptors = session.descriptorGroup2?.descriptors || [];
              const count = descriptors.length;
              const useMultiColumn = count > 4;
              const compactMode = count > 4;
              
              return (
                <div className={cn(
                  "grid gap-2",
                  useMultiColumn ? "grid-cols-2" : "grid-cols-1"
                )}>
                  {descriptors.map((desc) => {
                    const isSelected = lastEvent?.descriptors2?.includes(desc.id);
                    return (
                      <button
                        key={desc.id}
                        onClick={() => handleDescriptorToggle(2, desc.id)}
                        disabled={!lastEvent}
                        className={cn(
                          "w-full rounded-xl font-semibold transition-all border-2",
                          !lastEvent && "opacity-40 cursor-not-allowed",
                          isSelected
                            ? "bg-green-500 text-white border-green-700 shadow-lg"
                            : "bg-green-400 text-white border-transparent hover:bg-green-500 active:bg-green-600",
                          compactMode
                            ? "h-12 sm:h-14 text-xs sm:text-sm"
                            : "h-14 sm:h-16 lg:h-20 text-sm sm:text-base"
                        )}
                        data-testid={`desc2-btn-${desc.id}`}
                      >
                        <span className="flex items-center justify-center gap-1 sm:gap-2">
                          {isSelected && <Check className="w-3 sm:w-4 h-3 sm:h-4" />}
                          {desc.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </main>

      {/* Last Event Panel (fixed at bottom) */}
      {lastEvent && (
        <div className="bg-white border-t border-slate-200 px-3 sm:px-4 py-2 sm:py-3">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
                <div className="w-3 h-3 rounded bg-yellow-400 shrink-0" />
                <span className="font-medium text-slate-900 text-sm sm:text-base truncate max-w-[120px] sm:max-w-none">{lastEvent.eventTypeName}</span>
                <span className="text-xs sm:text-sm text-slate-500 font-mono shrink-0">
                  {formatRelativeTime(lastEvent.relativeTimestamp || 0)}
                </span>
                {lastEvent.descriptors1.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {lastEvent.descriptors1.map(d => {
                      const desc = session.descriptorGroup1.descriptors.find(x => x.id === d);
                      return desc && (
                        <Badge key={d} className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-xs">
                          {desc.name}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {lastEvent.descriptors2.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {lastEvent.descriptors2.map(d => {
                      const desc = session.descriptorGroup2.descriptors.find(x => x.id === d);
                      return desc && (
                        <Badge key={d} className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                          {desc.name}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {lastEvent.note && (
                  <span className="text-xs sm:text-sm text-slate-600 italic truncate max-w-[150px] sm:max-w-none">"{lastEvent.note}"</span>
                )}
              </div>
              
              <div className="flex items-center gap-2 shrink-0">
                {showNoteInput ? (
                  <div className="flex items-center gap-2">
                    <Textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note..."
                      className="w-32 sm:w-48 h-10 text-sm resize-none"
                      data-testid="note-input"
                    />
                    <SpeechToTextButton
                      onTranscribe={(text) => setNoteText(prev => prev ? `${prev} ${text}` : text)}
                      size="icon"
                    />
                    <Button size="icon" variant="ghost" onClick={handleAddNote}>
                      <Check className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setShowNoteInput(false)}>
                      <X className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowNoteInput(true)}
                    data-testid="add-note-btn"
                  >
                    <MessageSquare className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Note</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Intervention Count Badge */}
      <div className="fixed bottom-3 sm:bottom-4 right-3 sm:right-4 flex items-center gap-1.5 sm:gap-2 z-10">
        {/* Notes Panel Toggle (Phase 4) - neutral theme */}
        {session.enableObserverNotes !== false && (
          <Button
            onClick={() => setShowNotesPanel(!showNotesPanel)}
            variant={showNotesPanel ? "default" : "outline"}
            size="sm"
            className={cn(
              "shadow-lg",
              showNotesPanel ? "bg-slate-700 hover:bg-slate-800" : "bg-white hover:bg-slate-50 border-slate-300"
            )}
            data-testid="toggle-notes-panel"
          >
            <StickyNote className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Notes</span> {observerNotes.length > 0 && `(${observerNotes.length})`}
          </Button>
        )}
        <Badge className="bg-slate-900 text-white text-sm sm:text-lg px-2 sm:px-3 py-1" data-testid="event-count-badge">
          {session.events.length} <span className="hidden sm:inline">interventions</span>
        </Badge>
      </div>

      {/* Observer Notes Panel (Phase 4) - neutral theme */}
      {showNotesPanel && session.enableObserverNotes !== false && (
        <div className="fixed bottom-16 sm:bottom-20 right-3 sm:right-4 w-[calc(100%-1.5rem)] sm:w-80 max-w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-20" data-testid="notes-panel">
          <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-slate-50 rounded-t-lg">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2">
              <StickyNote className="w-4 h-4" />
              Observer Notes
            </h3>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setShowNotesPanel(false)}
              className="h-6 w-6"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Notes list */}
          <div className="max-h-60 overflow-y-auto p-3 space-y-2">
            {observerNotes.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                No notes yet. Add private notes below.
              </p>
            ) : (
              observerNotes.map((note) => (
                <div key={note.id} className="bg-slate-50 rounded-lg p-2 text-sm relative group">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-slate-700">{note.text}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                        <span className="font-mono">{formatRelativeTime(note.relativeTimestamp || 0)}</span>
                        <span>•</span>
                        <Badge variant="outline" className="text-xs py-0">
                          {note.partName}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDeleteObserverNote(note.id)}
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Add note input */}
          <div className="p-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
            <div className="flex gap-2">
              <Textarea
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
                placeholder="Add a private note..."
                className="flex-1 text-sm resize-none h-16"
                data-testid="observer-note-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddObserverNote();
                  }
                }}
              />
              <SpeechToTextButton
                onTranscribe={(text) => setCurrentNote(prev => prev ? `${prev} ${text}` : text)}
                className="self-start"
                size="sm"
              />
            </div>
            <Button
              onClick={handleAddObserverNote}
              disabled={!currentNote.trim()}
              className="w-full mt-2"
              size="sm"
              data-testid="save-observer-note"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Note
            </Button>
          </div>
        </div>
      )}

      {/* End Session Confirmation Dialog */}
      <AlertDialog open={showEndConfirm} onOpenChange={setShowEndConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Observation Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end this observation? 
              You have recorded <strong>{session.events.length} interventions</strong> over <strong>{formatTime(elapsedTime)}</strong>.
              <br /><br />
              The session will be marked as completed and saved to the cloud.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Continue Observing</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmEnd} 
              disabled={isSaving}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'End & Save Session'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
